import { Draft } from 'immer';
import { isEqual, cloneDeep } from 'lodash';
import { useEffect, useRef } from 'react';
import { FieldValues, useFormContext, useWatch } from 'react-hook-form';

import { CreditAgreement } from '@finley-technologies/finley-types';

import { useCreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';

type FormType = {
  wipForm: FieldValues;
  diffForm: FieldValues | undefined;
};

// This is a component, instead of a custom hook.
// useWatch will cause a rerender on every form change,
// so we are putting this functionality in a component to
// isolate these rerenders to only this component, instead of
// the whole parent component.
type AutoSaveProps<T extends FormType> = {
  onSave: (
    draft: Draft<CreditAgreement.DomainEntity.CreditAgreementFullArtifact>,
    values: T['wipForm'],
  ) => void;
};
export const AutoSave = <T extends FormType>({ onSave }: AutoSaveProps<T>) => {
  useWatch({ name: 'wipForm' }); // Rerender this component when any value of the form changes
  const { getValues } = useFormContext<T>();
  const updateCreditAgreement = useCreditAgreementStore(
    (store) => store.updateCreditAgreement,
  );

  const values = getValues();

  // Store previous values in a ref for later comparison
  const prevValuesRef = useRef<T['wipForm'] | null>(null);

  useEffect(() => {
    const wipValues = values.wipForm;
    // If previous values don't match current values, and this is not the first time
    // this is run, then schedule a CA update. Not, isEqual is needed because getValues()
    // produces a new object reference on every call. Also, note that isEqual can properly
    // handle the values of Date object that may be stored in the form.
    if (
      prevValuesRef.current !== null &&
      !isEqual(wipValues, prevValuesRef.current)
    ) {
      // Here we must cloneDeep the form values. react-hook-form can return stable references when using
      // useFieldArray, which breaks the functionality of isEqual.
      prevValuesRef.current = cloneDeep(wipValues);
      updateCreditAgreement((draft) => onSave(draft, wipValues));
    }

    prevValuesRef.current = cloneDeep(wipValues);
    // Return empty cleanup function when no update was scheduled
    return () => {};
  }, [values, updateCreditAgreement, onSave]);

  return null;
};
